import { useState, useEffect } from "react";
import { useCart } from "react-use-cart";
import { useRouter } from "next/router";
import Layout from "../../components/Layout";
import {
  ProminentTitle,
  MetaLabel,
  BodyCopySmall,
  ProductDescription,
} from "../../components/styles/TextStyles";
import { Button } from "../../components/FormElements";
import { getProductSlugs, getProduct } from "../../lib/shopify";
import { request } from "../../lib/datocms";
import Image from "next/image";
import Link from "next/link";
import Head from "next/head";
import styled from "styled-components";
import React from "react";
import { priceDecimal, priceRound } from "../../lib/formatPrice";
import { useStore } from "../_app";

export default function ProductPage({ data, globalSettings }) {
  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const { addItem, items, getItem, inCart } = useCart();
  const variant = data.variants.edges[0].node;

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("Sold");
  const [productStatus, setProductStatus] = useState(false);
  const [productStatusLabel, setProductStatusLabel] = useState("Sold");
  const [madeToOrderAvailable, setMadeToOrderAvailable] = useState(false);
  const [priceVisible, setPriceVisible] = useState(false);

  const setHeaderOpaque = useStore((state) => state.setHeaderOpaque);

  let isArtwork = false;
  data.collections.edges.map((collection) => {
    if (collection.node.handle === "artwork") {
      isArtwork = true;
    }
  });

  useEffect(() => {
    setHeaderOpaque();
  }, []);

  useEffect(() => {
    const itemInCart = getItem(variant.id);

    let madeToOrderOnly = false;
    let madeToOrderQty = 0;
    let readymadeQty = 0;

    // Loop through all the variants and find if
    // there is a Made to Order variant with inventory
    data.variants.edges.map((variant) => {
      if (
        variant.node.title === "Made to Order" &&
        variant.node.availableForSale
      ) {
        setMadeToOrderAvailable(true);
        madeToOrderQty = variant.node.quantityAvailable;

        if (madeToOrderQty === data.totalInventory) {
          madeToOrderOnly = true;
        }
      }
    });

    readymadeQty = data.totalInventory - madeToOrderQty;

    // Has the 'Disable Sales' environment toggle been activated?
    if (process.env.NEXT_PUBLIC_DISABLE_SALES === "true") {
      setButtonDisabled(true);
      setButtonLabel("Coming Soon");
      setProductStatus(false);
      setProductStatusLabel("Coming Soon");
      setPriceVisible(true);
    } else {
      // 'Pause All Sales' toggle is deactivated

      // Does this product have any stock?
      if (data.availableForSale) {
        // Are there any 'Made to Order' variants with stock?
        if (madeToOrderOnly) {
          setButtonDisabled(true);
          setButtonLabel("Out of readymade stock");
          setProductStatus(true);
          setProductStatusLabel("Made to Order");
          setPriceVisible(true);
        } else {
          // Is product already in customer's cart?
          if (inCart(variant.id)) {
            if (readymadeQty > itemInCart.quantity) {
              setButtonDisabled(false);
              setButtonLabel("Add another to cart");
            } else {
              setButtonDisabled(true);
              setButtonLabel("Last stock is in your cart");
            }
          } else {
            setButtonDisabled(false);
            setButtonLabel("Add to Cart");
          }
          setProductStatus(false);
          setProductStatusLabel("In Stock");
          setPriceVisible(true);
        }
      } else {
        // This product has no stock
        setButtonDisabled(true);
        setButtonLabel("Sold Out");
        setProductStatus(true);
        setProductStatusLabel("Sold");
        if (isArtwork) {
          setPriceVisible(false);
        } else {
          setPriceVisible(true);
        }
      }
    }
  }, [items]);

  // console.log("buttonDisabled: ", buttonDisabled);
  // console.log("buttonLabel: ", buttonLabel);
  // console.log("productStatus: ", productStatus);
  // console.log("productStatusLabel: ", productStatusLabel);
  // console.log("madeToOrderAvailable: ", madeToOrderAvailable);
  // console.log("madeToOrderOnly: ", madeToOrderOnly);

  let unformattedPrice = variant.priceV2.amount;

  let formattedPrice =
    unformattedPrice % 1
      ? priceDecimal.format(unformattedPrice)
      : priceRound.format(unformattedPrice);

  const addToCart = () => {
    return {
      id: variant.id,
      price: variant.priceV2.amount,
      currency: variant.priceV2.currencyCode,
      image: data.images.edges[0].node,
      title: data.title,
      handle: data.handle,
    };
  };

  ///const buttonLabel = inCart(variant.id) ? "Added to cart" : "Add to cart";

  const images = data.images.edges.reduce((result, image, i) => {
    // Removing the first result because it's used as the featured image
    if (i !== 0) {
      result.push(
        <Image
          src={image.node.originalSrc}
          width={image.node.width}
          height={image.node.height}
          key={image.node.id}
        />
      );
    }
    return result;
  }, []);

  return (
    <Layout>
      <Head>
        <title>veikkanen - Work - {data.title}</title>
        <meta property="og:title" content={data.title} />
      </Head>
      <FeaturedImageWrap>
        <Image
          src={data.images.edges[0].node.originalSrc}
          width={data.images.edges[0].node.width}
          height={data.images.edges[0].node.height}
        />
      </FeaturedImageWrap>
      <ProductInformation>
        <ProductInformationInner>
          <ProminentTitle as="h1">{data.title}</ProminentTitle>
          <MetaLabel>
            {priceVisible && formattedPrice}
            {productStatus && (
              <ProductStatus status={productStatusLabel}>
                {" "}
                • {productStatusLabel}
              </ProductStatus>
            )}
          </MetaLabel>

          <ProductDescription
            dangerouslySetInnerHTML={{ __html: data.descriptionHtml }}
          ></ProductDescription>

          {buttonDisabled ? (
            <Button disabled>{buttonLabel}</Button>
          ) : (
            <Button
              onClick={() => addItem(addToCart(), 1)}
              disabled={buttonDisabled}
            >
              {buttonLabel}
            </Button>
          )}
          {madeToOrderAvailable && (
            <AdditionalInfo>
              Limited made to order editions available.
              <br />
              <a
                href={`mailto:hello@veikkanen.com?subject=Made to Order Enquiry: ${data.title}`}
              >
                Enquire by email.
              </a>
            </AdditionalInfo>
          )}
          {isArtwork && (
            <AdditionalInfo>
              Commissions available upon request.
              <br />
              See{" "}
              <Link href={"/commissions"}>
                <a>commissions page</a>
              </Link>{" "}
              for more information.
            </AdditionalInfo>
          )}
        </ProductInformationInner>
      </ProductInformation>

      <ProductImageGrid>{images}</ProductImageGrid>
    </Layout>
  );
}

export async function getStaticProps({ params }) {
  const data = await getProduct(params.product);
  const globalSettings = await request({
    query: `query GlobalSettings {
      global {
        pauseAllSales
        newDropDate
      },
    }`,
  });

  return {
    props: {
      data,
      globalSettings,
    },
    revalidate: 10,
  };
}

export async function getStaticPaths() {
  const productSlugs = await getProductSlugs();

  const paths = productSlugs.map((slug) => {
    const product = String(slug.node.handle);
    return {
      params: { product },
    };
  });

  return {
    paths,
    fallback: true,
  };
}

const ImageGallery = styled.div``;

const ProductInformation = styled.div`
  display: grid;
  padding: var(--gridMarginGap);
  column-gap: var(--gridColumnGap);

  @media screen and (min-width: 768px) {
    position: fixed;
    grid-template-columns: repeat(6, 1fr);
    z-index: 200;
    top: 0;
    right: 0;
    width: 50vw;
    height: 100vh;
    align-content: center;
  }
`;

const ProductInformationInner = styled.div`
  display: grid;
  justify-items: start;

  @media screen and (min-width: 768px) {
    grid-column: 2 / span 4;
  }
`;

const FeaturedImageWrap = styled.div`
  display: grid;

  margin-top: var(--headerHeight);

  grid-template-columns: repeat(1, 1fr);
  column-gap: var(--gridColumnGap);
  row-gap: var(--gridMarginGap);

  @media screen and (min-width: 768px) {
    left: 0;
    width: 50vw;
    padding: 0 var(--gridMarginGap);
  }
`;

const ProductImageGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(1, 1fr);
  padding: var(--gridMarginGap);
  column-gap: var(--gridColumnGap);
  row-gap: var(--gridMarginGap);

  @media screen and (min-width: 768px) {
    left: 0;
    width: 50vw;
  }
`;

const ProductStatus = styled.span`
  color: ${(props) =>
    props.status === "Sold"
      ? `var(--colorBrandOrange)`
      : `var(--colorBrandPink)`};
`;

const AdditionalInfo = styled.span`
  font-size: var(--step-1-5);
  color: var(--colorContentPrimary);

  & > a {
    text-decoration: underline;
    &:hover {
      cursor: pointer;
      color: var(--colorBrandPink);
    }
  }
`;
